import { registerMutation, useMutation } from 'data-fetching-client';
import { ViewDraft } from '../model/viewDraft';
import { useClientStorage, useDraftContext, useBypassStorage } from '../data/VizCoreProvider';
import { useMemo, useCallback } from 'react';
import { getSegmentQueryInternal } from './useSegment';
import { currentViewQuery } from './view-hooks';
export const mutateSegmentMutation = registerMutation({
  fieldName: 'vizcoreSegment',
  fetcher: async ({
    segment,
    draftContext,
    clientStorage,
    bypassStorage
  }) => {
    var _draftContext$editHas;
    // QUICK FIX: Skip storage operations to prevent rollbacks
    if (bypassStorage) {
      return {
        segmentHash: 'bypass-mode',
        segment,
        draftContext: Object.assign({}, draftContext, {
          editHash: draftContext.editHash || 'bypass-draft-active'
        })
      };
    }

    // Save segment and get hash
    const segmentHash = await segment.toClientCache(clientStorage);

    // Load and update view draft reference
    const existingDraft = await ViewDraft.fromClientCache((_draftContext$editHas = draftContext.editHash) !== null && _draftContext$editHas !== void 0 ? _draftContext$editHas : '',
    // there is no empty string hash, so effectively existingDraft will be null
    clientStorage).catch(() => null);

    // Create or update view draft with new segment hash
    const baseDraft = existingDraft !== null && existingDraft !== void 0 ? existingDraft : new ViewDraft({
      viewId: draftContext.viewId
    });
    const updatedDraft = baseDraft.with({
      segmentHashOrId: segmentHash
    });

    // Save updated view draft
    const viewDraftHash = await updatedDraft.toClientCache(clientStorage);
    return {
      segmentHash,
      segment,
      draftContext: Object.assign({}, draftContext, {
        editHash: viewDraftHash
      })
    };
  },
  args: ['segment', 'draftContext', 'clientStorage', 'bypassStorage']
});
const EMPTY_OBJECT = {};

/**
 * Hook to save and manage segment drafts.
 *
 * This hook is built on Data Fetching Client (DFC) but provides a cleaner API
 * for segment mutations. It handles all the complexity of saving segment drafts,
 * managing the edit hash, and updating client-side storage.
 *
 * ## Draft Persistence
 *
 * When a segment is modified with this hook:
 *
 * 1. The updated segment is saved to client-side storage with a unique hash
 * 3. A composite representation of the persisted Segment and Viz Settings is saved to client-side storage with a unique hash
 * 4. The editHash is automatically updated in context. This is equivalent to the "version key" of a form.
 *
 * This creates a complete draft cycle, with useSegment reading from the same draft that
 * useMutateSegment writes to.
 *
 * Note that this does not actually saves to backend, it only persists to client-side storage.
 * In order to save to backend, you need to use the `useSaveViewDraft` hook.
 *
 * ## Integration with useSegment
 *
 * This hook is designed to work seamlessly with useSegment:
 *
 * ```tsx
 * // Read segment data
 * const { data: segment } = useSegment();
 *
 * // Write segment data
 * const [saveSegment] = useMutateSegment();
 * ```
 *
 * ## Basic Usage
 *
 * ```tsx
 * const [saveSegment, { loading }] = useMutateSegment({
 *   onSuccess: ({ segment }) => {
 *     console.log('Saved segment', segment);
 *   },
 *   onError: (error) => {
 *     console.error('Failed to save segment', error);
 *   }
 * });
 *
 * // Update an existing segment
 * saveSegment(segment.with({ search: "new term" }));
 * ```
 *
 * ## Handling Multiple Properties
 *
 * Use the `with()` method to update multiple properties at once:
 *
 * ```tsx
 * saveSegment(segment.with({
 *   search: "new search",
 *   pipelineId: "123",
 *   sorts: [{ property: "createdate", order: "DESC" }]
 * }));
 * ```
 *
 * ## Working with Promises
 *
 * The saveSegment function returns a Promise that resolves when the save is complete:
 * You can either use it directly, or use the `onSuccess` and `onError` callbacks available on the hook options.
 *
 * ```tsx
 * saveSegment(updatedSegment)
 *   .then(result => {
 *     // Handle successful save
 *     console.log('Segment saved:', result.data.vizcoreMutateSegment.segment);
 *   })
 *   .catch(error => {
 *     // Handle save error
 *     console.error('Save failed:', error);
 *   });
 * ```
 *
 * ## Best Practices
 *
 * 1. **Always use segment's `with()` method for immutable updates:**
 *    ```tsx
 *    // Good - creates a new Segment object with updated properties
 *    saveSegment(segment.with({ search: newTerm }));
 *
 *    // Bad - mutating the segment directly
 *    segment.search = newTerm;
 *    saveSegment(segment);
 *    ```
 *
 * 2. **Handle loading state for better UX:**
 *    ```tsx
 *    const [saveSegment, { loading }] = useMutateSegment();
 *
 *    return (
 *      <Button
 *        onClick={() => saveSegment(updatedSegment)}
 *        disabled={loading}
 *      >
 *        {loading ? 'Saving...' : 'Save'}
 *      </Button>
 *    );
 *    ```
 *
 * For more details about the Segment model, see the Segment class JSDoc
 * in core/model/segment.ts.
 *
 * ## Optimistic Updates
 *
 * We are working on implementing optimistic updates for this hook. Because it takes a while to generate the segment hash, and writes to client-side storage, it might not be fitting to store rapidly changing data, such as "search" field.
 * To mitigate this, you can create a local useState variable to drive the controlled Input value along side writing to this hook.
 *
 * @see Segment
 * @see useSegment
 */
export function useMutateSegment(options = EMPTY_OBJECT) {
  const [globalDraftContext, onDraftChange] = useDraftContext();
  const clientStorage = useClientStorage();
  const bypassStorage = useBypassStorage();
  const memoizedOptions = useMemo(() => {
    const {
      onError,
      onSuccess
    } = options;
    return {
      onError,
      onCompleted: data => {
        // In bypass mode, only call onDraftChange when transitioning from null to bypass hash
        if (bypassStorage) {
          if (globalDraftContext.editHash === null) {
            onDraftChange(data.vizcoreSegment.draftContext);
          }
        } else {
          // Normal mode: mutations always generate new hashes, so always call onDraftChange
          onDraftChange(data.vizcoreSegment.draftContext);
        }
        onSuccess === null || onSuccess === void 0 || onSuccess(data.vizcoreSegment.segment);
      }
    };
  }, [options, onDraftChange, bypassStorage, globalDraftContext]);
  const [mutate, result] = useMutation(mutateSegmentMutation, memoizedOptions);
  const saveSegment = useCallback((segment, draftContext) => {
    const effectiveDraftContext = draftContext !== null && draftContext !== void 0 ? draftContext : globalDraftContext;
    return mutate({
      variables: {
        segment,
        draftContext: effectiveDraftContext,
        clientStorage,
        bypassStorage
      },
      optimisticResponse(vars) {
        return {
          vizcoreSegment: {
            segmentHash: 'bypass-draft-active',
            segment,
            draftContext: vars.draftContext
          }
        };
      },
      update(cache, {
        data
      }) {
        var _cache$readQuery;
        const view = (_cache$readQuery = cache.readQuery({
          query: currentViewQuery,
          variables: {
            viewId: effectiveDraftContext.viewId,
            objectTypeId: effectiveDraftContext.objectTypeId,
            clientStorage
          }
        })) === null || _cache$readQuery === void 0 ? void 0 : _cache$readQuery.vizcoreCurrentView;
        if (data !== null && data !== void 0 && data.vizcoreSegment && view) {
          cache.writeQuery({
            query: getSegmentQueryInternal,
            variables: {
              draftContext: Object.assign({}, effectiveDraftContext, {
                editHash: data.vizcoreSegment.draftContext.editHash
              }),
              clientStorage,
              view,
              pipelinePropertyName: data.vizcoreSegment.segment.pipelinePropertyName
            },
            data: {
              vizcoreSegment: data.vizcoreSegment.segment
            }
          });
        }
      }
    }).then(r => {
      var _r$data;
      return (_r$data = r.data) === null || _r$data === void 0 || (_r$data = _r$data.vizcoreSegment) === null || _r$data === void 0 ? void 0 : _r$data.segment;
    });
  }, [mutate, globalDraftContext, clientStorage, bypassStorage]);
  return useMemo(() => {
    return [saveSegment, result];
  }, [saveSegment, result]);
}